/**
 * Privacy utilities for coordinate masking and address truncation
 */

import {
  PrivacyMode,
  CoordinateFormat,
  DMSCoordinate,
  PrivacySettings,
  DEFAULT_PRIVACY_SETTINGS,
  PRIVACY_STORAGE_KEY
} from '../types/PrivacyTypes';
import { getLocalStorageItem, setLocalStorageItem } from './localStorageUtils';

// ============================================
// Coordinate Formatting & Masking
// ============================================

/**
 * Convert decimal degrees to DMS (Degrees, Minutes, Seconds)
 */
export function decimalToDMS(decimal: number, type: 'lat' | 'lon'): DMSCoordinate {
  const absolute = Math.abs(decimal);
  let degrees = Math.floor(absolute);
  const minutesDecimal = (absolute - degrees) * 60;
  let minutes = Math.floor(minutesDecimal);
  let seconds = Math.round((minutesDecimal - minutes) * 60);

  // Handle overflow: 60 seconds -> 1 minute
  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }
  // Handle overflow: 60 minutes -> 1 degree
  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }

  let direction: 'N' | 'S' | 'E' | 'W';
  if (type === 'lat') {
    direction = decimal >= 0 ? 'N' : 'S';
  } else {
    direction = decimal >= 0 ? 'E' : 'W';
  }

  return { degrees, minutes, seconds, direction };
}

/**
 * Convert DMS to decimal degrees
 */
export function dmsToDecimal(dms: DMSCoordinate): number {
  const decimal = dms.degrees + dms.minutes / 60 + dms.seconds / 3600;
  return (dms.direction === 'S' || dms.direction === 'W') ? -decimal : decimal;
}

/**
 * Format DMS coordinate as string
 * @param masked - if true, mask the seconds portion
 */
export function formatDMS(dms: DMSCoordinate, masked: boolean = false): string {
  const minutesStr = masked
    ? `${dms.minutes.toString().padStart(2, '0').charAt(0)}•`
    : dms.minutes.toString().padStart(2, '0');
  const secondsStr = masked
    ? '••"'
    : `${dms.seconds.toFixed(0).padStart(2, '0')}"`;

  return `${dms.degrees}° ${minutesStr}' ${secondsStr} ${dms.direction}`;
}

/**
 * Format decimal coordinate with optional masking
 * @param value - decimal coordinate value
 * @param masked - if true, mask last 5 digits
 */
export function formatDecimalCoordinate(value: number, masked: boolean = false): string {
  const formatted = value.toFixed(6);

  if (!masked) {
    return formatted;
  }

  // Mask last 5 digits: "12.9•••••" (show only 1 decimal place)
  const parts = formatted.split('.');
  if (parts.length === 2) {
    const intPart = parts[0];
    const decPart = parts[1];
    const visibleDecimals = decPart.substring(0, 1);
    return `${intPart}.${visibleDecimals}•••••`;
  }

  return formatted;
}

/**
 * Format coordinates based on format and masking settings
 */
export function formatCoordinates(
  latitude: number,
  longitude: number,
  format: CoordinateFormat,
  masked: boolean
): { latitude: string; longitude: string } {
  if (format === 'dms') {
    const latDMS = decimalToDMS(latitude, 'lat');
    const lonDMS = decimalToDMS(longitude, 'lon');
    return {
      latitude: formatDMS(latDMS, masked),
      longitude: formatDMS(lonDMS, masked)
    };
  }

  return {
    latitude: formatDecimalCoordinate(latitude, masked),
    longitude: formatDecimalCoordinate(longitude, masked)
  };
}

// ============================================
// Address Privacy
// ============================================

/**
 * Truncate address to city/country level for privacy
 * Handles various address formats from geocoding APIs
 */
export function formatAddressForPrivacy(fullAddress: string, mode: PrivacyMode): string {
  if (mode === 'precise') {
    return fullAddress;
  }

  if (!fullAddress || fullAddress.trim() === '') {
    return '';
  }

  // Split by comma and extract relevant parts
  const parts = fullAddress.split(',').map(p => p.trim());

  if (parts.length <= 2) {
    // Already short enough (e.g., "Mumbai, India")
    return fullAddress;
  }

  // Try to extract city and country (usually last 2-3 parts)
  // Common formats:
  // "123 Street, District, City, State, Country"
  // "Location, City, Country"

  // Take last 2 parts (usually city/state and country)
  const cityCountry = parts.slice(-2).join(', ');

  // If the result looks like just numbers/postal codes, try different approach
  if (/^\d+/.test(cityCountry.split(',')[0]?.trim() || '')) {
    // Postal code detected, take parts before it
    const filtered = parts.filter(p => !/^\d{4,}/.test(p.trim()));
    return filtered.slice(-2).join(', ');
  }

  return cityCountry;
}

/**
 * Extract just the city name from an address
 */
export function extractCity(fullAddress: string): string {
  if (!fullAddress) return '';

  const parts = fullAddress.split(',').map(p => p.trim());

  // Usually city is 2nd to last or 3rd to last part
  if (parts.length >= 3) {
    // Check if last part is country, and second to last is state/region
    return parts[parts.length - 3] || parts[parts.length - 2] || '';
  }

  if (parts.length >= 2) {
    return parts[0];
  }

  return fullAddress;
}

/**
 * Extract just the country from an address
 */
export function extractCountry(fullAddress: string): string {
  if (!fullAddress) return '';

  const parts = fullAddress.split(',').map(p => p.trim());
  return parts[parts.length - 1] || '';
}

// ============================================
// Settings Persistence
// ============================================

/**
 * Load privacy settings from localStorage
 */
export function loadPrivacySettings(): PrivacySettings {
  const saved = getLocalStorageItem<Partial<PrivacySettings> | null>(PRIVACY_STORAGE_KEY, null);
  return saved ? { ...DEFAULT_PRIVACY_SETTINGS, ...saved } : DEFAULT_PRIVACY_SETTINGS;
}

/**
 * Save privacy settings to localStorage
 */
export function savePrivacySettings(settings: Partial<PrivacySettings>): void {
  const current = loadPrivacySettings();
  const updated = { ...current, ...settings };
  setLocalStorageItem(PRIVACY_STORAGE_KEY, updated);
}

/**
 * Update a single privacy setting
 */
export function updatePrivacySetting<K extends keyof PrivacySettings>(
  key: K,
  value: PrivacySettings[K]
): void {
  savePrivacySettings({ [key]: value });
}

// ============================================
// DMS Parsing
// ============================================

/**
 * Parse DMS string input to DMSCoordinate
 * Accepts formats like: "12° 58' 17" N", "12 58 17 N", "12°58'17"N"
 */
export function parseDMSString(input: string): DMSCoordinate | null {
  // Remove extra spaces and normalize
  const normalized = input.trim().toUpperCase();

  // Match pattern: degrees, minutes, seconds, direction
  const patterns = [
    /(\d+)[°\s]+(\d+)['\s]+(\d+(?:\.\d+)?)["\s]*([NSEW])/,
    /(\d+)\s+(\d+)\s+(\d+(?:\.\d+)?)\s*([NSEW])/
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match) {
      return {
        degrees: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
        seconds: parseFloat(match[3]),
        direction: match[4] as 'N' | 'S' | 'E' | 'W'
      };
    }
  }

  return null;
}

/**
 * Parse coordinate pair string
 * Accepts: "12.9716, 77.5946" or "12° 58' 17" N, 77° 35' 40" E"
 */
export function parseCoordinatePairString(input: string): { lat: number; lng: number } | null {
  // Try decimal format first
  const decimalMatch = input.match(/(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)/);
  if (decimalMatch) {
    const lat = parseFloat(decimalMatch[1]);
    const lng = parseFloat(decimalMatch[2]);
    if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng };
    }
  }

  // Try DMS format
  const dmsPattern = /(\d+[°\s]+\d+['\s]+\d+(?:\.\d+)?["\s]*[NS])\s*[,\s]\s*(\d+[°\s]+\d+['\s]+\d+(?:\.\d+)?["\s]*[EW])/i;
  const dmsMatch = input.match(dmsPattern);
  if (dmsMatch) {
    const latDMS = parseDMSString(dmsMatch[1]);
    const lonDMS = parseDMSString(dmsMatch[2]);
    if (latDMS && lonDMS) {
      return {
        lat: dmsToDecimal(latDMS),
        lng: dmsToDecimal(lonDMS)
      };
    }
  }

  return null;
}
